import { Component, ChangeDetectionStrategy } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { CsPage, NavController } from 'providers/nav-controller';
import { ExerciseSlot, ExerciseSlotEntry } from 'services/exercise-db.service';
import { ExerciseDbService, Exercise, ExerciseTextLanguage } from 'services/exercise-db.service';
import { DatabaseWrapper } from 'services/database-wrapper';
import { UnsavedChangesGuardProvider, UnsavedChangesGuard } from 'providers/unsaved-changes-guard/unsaved-changes-guard';
import { AuthService } from 'services/auth.service';
import { AuthRole } from '@cf-shared/couch-auth';
import { generateIdFromName } from '@cf-shared/shared-util';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { AdminEditExerciseSlotPageParams } from './admin-edit-exercise-slot-interface';

@Component({
    selector: 'page-admin-edit-exercise-slot',
    templateUrl: 'admin-edit-exercise-slot.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ModalPageMarker]
})
export class AdminEditExerciseSlotPage extends CsPage<AdminEditExerciseSlotPageParams> {
  slot: ExerciseSlot;
  protected globalExercises: Exercise[];
  protected globalTestExercises: Exercise[];
  private unsaved: UnsavedChangesGuard;

  constructor(
    public navCtrl: NavController,
    private unsavedProvider: UnsavedChangesGuardProvider,
    protected exerciseDb: ExerciseDbService,
    protected auth: AuthService,
  ) {
    super();
  }

  async ionViewCanEnter() {
    await this.auth.pageAuthGuardAsync(AuthRole.Dev);

    let id = this.navParams.data.id;
    if (id) {
      // withDocWatcherAsync, not a hand-written create/destroy: the read can throw when the
      // database is unavailable, and the watcher must still be released
      this.slot = await this.exerciseDb.database.withDocWatcherAsync(watcher =>
        this.exerciseDb.slotForIdAsync(id, watcher));
      if (!this.slot) {
        return this.auth.failPageGuardAsync(`Unknown exercise id: '${id}'`);
      }
    } else {
      this.slot = new ExerciseSlot();
    }

    this.unsaved = this.unsavedProvider.createGuard([this.slot], async () => {
      // clear empty on exit
      this.slot.exercises = this.slot.exercises.filter(e => e.exerciseId != null && e.exerciseId != '');
      await this.exerciseDb.saveSlotAsync(this.slot, DatabaseWrapper.getTransactionId());
    });

    // pin English — an admin screen, and English is the authoring locale for
    // curated exercise text (#4210)
    this.globalExercises = await this.exerciseDb.getAllExercisesAsync(undefined, ExerciseTextLanguage.Authoring);
    this.globalTestExercises = this.globalExercises.filter(e => e.standard);
  }

  nameChanged() {
    // if never saved before
    if (this.slot._rev == null) {
      // autogenerate id from name
      this.slot._id = 'slot/$' + generateIdFromName(this.slot.slotName, '_');
    }
  }

  addExercise() {
    this.slot.exercises.push(new ExerciseSlotEntry());
  }

  removeExercise(e: ExerciseSlotEntry) {
    let i = this.slot.exercises.findIndex(f => f == e);
    this.slot.exercises.splice(i, 1);
  }

  addTestExercise() {
    if (!this.slot.testExercises || !this.slot.testExercises.length) {
      this.slot.testExercises = [];
    }
    this.slot.testExercises.push(new ExerciseSlotEntry());
  }

  removeTestExercise(e: ExerciseSlotEntry) {
    let i = this.slot.testExercises.findIndex(f => f == e);
    this.slot.testExercises.splice(i, 1);
  }

  handleSaveAndExit(event: Event) {
    lockClick(event, this.unsaved.saveAndExitAsync());
  }

  handleCancel(event: Event) {
    lockClick(event, this.navCtrl.popAsync());
  }

  async ionViewCanLeave() {
    return this.unsaved.confirmLeaveAsync();
  }
}
